// Utility functions for printing tickets, extracted for reuse in class components.
// All functions are pure and require state and helpers as arguments.

// Helper to get all payloads
export function getAllPayloads({ bookingSummary, ticketsDataForTicketSummary, fnbDataForTicketSummary }, getMethods) {
  const orderDetails = bookingSummary;
  if (getMethods === 'getdate') {
    if (!orderDetails.session?.showdate) return '';
    const getDate = orderDetails.session.showdate;
    const dateSplit = getDate.split('T')[0].split('-');
    return `${dateSplit[1]}/${dateSplit[2]}/${dateSplit[0]}`;
  } else if (getMethods === 'getseats') {
    const setSeatsArray = [];
    if (Object.keys(orderDetails).length > 0) {
      orderDetails.order_grouping?.forEach(seatsData => {
        if (seatsData.type?.toLowerCase() === 'ticket') {
          setSeatsArray.push(seatsData.seats);
        }
      });
      return setSeatsArray.join(', ');
    }
    return '';
  } else if (getMethods === 'gettickets') {
    const setTicketArray = [];
    if (ticketsDataForTicketSummary?.length > 0) {
      ticketsDataForTicketSummary.forEach(tickets => {
        const setFormat = `${tickets.description} X ${tickets.qty}~${tickets.unitprice.toFixed(2)}`;
        setTicketArray.push(setFormat);
      });
      return setTicketArray;
    }
    return [];
  } else if (getMethods === 'getfnb') {
    const setFnbsArray = [];
    if (fnbDataForTicketSummary?.length > 0) {
      fnbDataForTicketSummary.forEach(fnbData => {
        const modifierArray = [];
        let setFormat = `${fnbData.description} X ${fnbData.qty}`;
        let totalUnitprice = fnbData.unitprice;
        let totalModifiePrice = 0;
        if (fnbData.modifiers?.length > 0) {
          fnbData.modifiers.forEach(modData => {
            const modifierFormat = `${modData.description}(${modData.qty})`;
            modifierArray.push(modifierFormat);
            totalModifiePrice += modData.unitprice * modData.qty;
          });
          totalUnitprice += totalModifiePrice;
          setFormat += ` | ${modifierArray.join(', ')}`;
        }
        setFormat += ` ~ ${totalUnitprice.toFixed(2)}`;
        setFnbsArray.push(setFormat);
      });
      return setFnbsArray;
    }
    return [];
  } else if (getMethods === 'paymetTypes') {
    const setPaymetTypesArray = [];
    if (orderDetails.bookings_paymenttypes?.length > 0) {
      orderDetails.bookings_paymenttypes.forEach(paymentTypesData => {
        let setFormat = paymentTypesData.pgName + ' | ';
        if (paymentTypesData.cardinformation && paymentTypesData.cardinformation.cardNumber) {
          setFormat += paymentTypesData.cardinformation.cardNumber;
        }
        setFormat += ' ~';
        setFormat += paymentTypesData.amount.toFixed(2);
        setPaymetTypesArray.push(setFormat);
      });
    }
    return setPaymetTypesArray;
  } else if (getMethods === 'getticketsForIndividualPrint') {
    const order_items = orderDetails?.bookings_items?.filter(item => item.type?.toLowerCase() === 'ticket') || [];
    const individualTicketsArray = [];
    order_items.forEach(ticket => {
      individualTicketsArray.push(
        `${ticket.seats}@${ticket.description} X ${ticket.quantity}~${ticket.additionalInfo.hasOwnProperty('ticketPrice') ? ticket.additionalInfo.ticketPrice.toFixed(2) : ''}`
      );
    });
    return individualTicketsArray;
  }
  return undefined;
}

// Helper to calculate total
export function calculateTotal({ ticketsDataForTicketSummary, fnbDataForTicketSummary, bookingSummary }) {
  let totalTicketPricing = 0,
    totalFnbPricing = 0,
    grandTotal = 0,
    bookingFee = bookingSummary.bookingfeebeforetax || 0,
    tax = bookingSummary.taxValue || 0;

  if (ticketsDataForTicketSummary?.length > 0) {
    ticketsDataForTicketSummary.forEach(eachTicket => {
      totalTicketPricing += eachTicket.qty * (eachTicket.unitprice || 0);
    });
  }
  grandTotal += totalTicketPricing;
  if (fnbDataForTicketSummary?.length > 0) {
    fnbDataForTicketSummary.forEach(eachFnb => {
      let unitprice = eachFnb.unitprice || 0;
      if (eachFnb.modifiers?.length > 0) {
        eachFnb.modifiers.forEach(modifier => {
          let totalModifiePrice = 0;
          totalModifiePrice += (modifier.unitprice || 0) * (modifier.qty || 0);
          unitprice += totalModifiePrice;
        });
      }
      totalFnbPricing += (eachFnb.qty || 0) * unitprice;
    });
  }
  grandTotal += totalFnbPricing;
  grandTotal += bookingFee;
  grandTotal += tax;
  return grandTotal.toFixed(2);
}

// Main print handler
export function handlePrintTicketUtil({
  bookingSummary,
  ticketsSummary,
  ticketsDataForTicketSummary,
  fnbDataForTicketSummary,
  getAllPayloadsFn,
  calculateTotalFn,
  formatShowTimeFn,
  windowObj,
  constantObj,
}) {
  if (Object.keys(bookingSummary).length > 0 && ticketsSummary.length > 0) {
    const orderDetails = bookingSummary;
    const printdetails = {
      orderid: orderDetails.id,
      bookingid: orderDetails.bookingid,
      bookingdatetime: orderDetails.hasOwnProperty('createdAt') ? orderDetails.createdAt : '',
      cinemaname: orderDetails.cinema && orderDetails.cinema.cinemas_attributes && orderDetails.cinema.cinemas_attributes.length > 0 ? orderDetails.cinema.cinemas_attributes[0].name : '',
      moviename: orderDetails.film && orderDetails.film.films_attributes && orderDetails.film.films_attributes.length > 0 ? orderDetails.film.films_attributes[0].title : '',
      screen: orderDetails.session && orderDetails.session.cinemas_screens && orderDetails.session.cinemas_screens.length > 0 ? orderDetails.session.cinemas_screens[0].name : '',
      showdate: orderDetails.session && orderDetails.session.showdate ? getAllPayloadsFn({ bookingSummary, ticketsDataForTicketSummary, fnbDataForTicketSummary }, 'getdate') : '',
      showtime: orderDetails.session && orderDetails.session.showtime ? formatShowTimeFn(orderDetails.session.showtime) : '',
      allseats: getAllPayloadsFn({ bookingSummary, ticketsDataForTicketSummary, fnbDataForTicketSummary }, 'getseats'),
      currency: '$',
      groupingtickets: ticketsDataForTicketSummary.length > 0 ? getAllPayloadsFn({ bookingSummary, ticketsDataForTicketSummary, fnbDataForTicketSummary }, 'gettickets') : [],
      fnborderno: orderDetails.pickupNumber ? orderDetails.pickupNumber : '',
      fnbitems: fnbDataForTicketSummary.length > 0 ? getAllPayloadsFn({ bookingSummary, ticketsDataForTicketSummary, fnbDataForTicketSummary }, 'getfnb') : [],
      subtotal: ticketsSummary[0].subTotal,
      taxheading: '',
      totaltax: ticketsSummary[0].taxValue,
      grandtotal: calculateTotalFn({ ticketsDataForTicketSummary, fnbDataForTicketSummary, bookingSummary }),
      paymentmethods: getAllPayloadsFn({ bookingSummary, ticketsDataForTicketSummary, fnbDataForTicketSummary }, 'paymetTypes') || [],
      transactionno: orderDetails.ticketingTransactionId,
      qrcodeurl: orderDetails.qrcodeurl,
      rating: orderDetails.film && orderDetails.film.rating != null ? orderDetails.film.rating : '',
      singletickets: getAllPayloadsFn({ bookingSummary, ticketsDataForTicketSummary, fnbDataForTicketSummary }, 'getticketsForIndividualPrint'),
      format: constantObj.ENABLE_INDIVIDUAL_TICKETS_PRINT === 'true' ? 'individual' : '',
    };
    console.log('Print Details Object', printdetails);

    // Determine print flow type
    let getFlows = '';
    if (ticketsDataForTicketSummary.length > 0 && fnbDataForTicketSummary.length === 0) {
      getFlows = 'TICKETS_ONLY';
    } else if (ticketsDataForTicketSummary.length === 0 && fnbDataForTicketSummary.length > 0) {
      getFlows = 'FNB_ONLY';
    } else if (ticketsDataForTicketSummary.length > 0 && fnbDataForTicketSummary.length > 0) {
      getFlows = 'TICKETS_WITH_FNB';
    }

    console.log('callPrint > printtype', getFlows);
    console.log('callPrint > orderdetails', printdetails);
    windowObj?.callbackObj?.callPrint(JSON.stringify(printdetails), getFlows);
  }
}
